async function updateHeaderCartCount() {
  const res = await fetch(`${window.Shopify.routes.root}cart.json`)
  const cart = await res.json()

  const countElement = document.getElementById('cart-count')
  if (cart.item_count === 0) {
    countElement.hidden = true
  } else {
    countElement.textContent = cart.item_count
    countElement.hidden = false
  }
}

async function updateCartQuantity(keys, quantity, callback) {
  //create an updates object with the same quantity for all keys
  const updates = Object.fromEntries(keys.map((key) => [key, quantity]))

  await fetch(`${window.Shopify.routes.root}cart/update.js`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ updates })
  })

  callback()
  updateHeaderCartCount()
}

const updateCartQuantityDebounced = debounce(
  (productKeys, quantity, callback) =>
    updateCartQuantity(productKeys, quantity, callback)
)

//special price products are added alongside a 'main product', so they have to be removed with it
async function updateItem(productKey, quantity, callback) {
  const cartData = await fetch(`${window.Shopify.routes.root}cart.json`).then(
    (res) => res.json()
  )
  const item = cartData.items.find((i) => i.key === productKey)
  const additionalKeys = []

  if (item) {
    if (quantity === 0) {
      if (item.properties._voucher) {
        //if an item with a voucher is in the cart, and it is removed, reset the voucher.
        try {
          await fetch(`https://app.insectlore.co.uk/api/v1/vouchers/reset`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              voucher_code: item.properties._voucher,
              sku: item.sku
            })
          })
        } catch (error) {
          console.log('error resetting voucher: ', error)
          if (window.Sentry) {
            Sentry.captureException(error, {
              tags: { function: 'resetVoucher' }
            })
          }
        }
      }

      //if special price products have been added with this item, remove the special price products
      cartData.items.forEach((i) => {
        if (i.properties._parent_product_id === item.product_id) {
          additionalKeys.push(i.key)
        }
      })
    }
  }

  updateCartQuantityDebounced(
    [productKey, ...additionalKeys],
    quantity,
    callback
  )
}

//this is required to deal with the scenario where a user increments/decrements the quantity quickly
function debounce(func, timeout = 500) {
  let timer
  return (...args) => {
    clearTimeout(timer)
    timer = setTimeout(() => {
      func.apply(this, args)
    }, timeout)
  }
}

function handleQuantityButtons(cartFormElement, callback) {
  cartFormElement
    .querySelectorAll('.input-number-wrapper input')
    .forEach((input) => {
      input.addEventListener('change', async function () {
        const key = input
          .closest('[data-item-key]')
          .getAttribute('data-item-key')
        updateItem(key, this.value, callback)
      })
    })

  cartFormElement.querySelectorAll('button.remove').forEach((button) => {
    button.addEventListener('click', async function () {
      const key = button
        .closest('[data-item-key]')
        .getAttribute('data-item-key')
      updateItem(key, 0, callback)
    })
  })
}

function openCartDrawer() {
  document.querySelector('html').classList.add('cart-open')
  window.initCartRecommendationsSwipers()
}

function customInputNumberListeners(form) {
  const minimumValue = form.getAttribute('action').endsWith('/cart') ? 0 : 1
  form.querySelectorAll('.input-number-wrapper button').forEach((button) => {
    button.addEventListener('click', () => {
      const inputElement = button.parentElement.querySelector('input')
      let currentQuantity = Number(inputElement.value)
      const increment = button.classList.contains('plus')
      //this increments and decrements (with a minimum value of 0)
      const newQuantity = increment
        ? currentQuantity + 1
        : Math.max(currentQuantity - 1, minimumValue)

      inputElement.value = newQuantity //set value in ui immediately
      inputElement.dispatchEvent(new Event('change', { bubbles: true }))
    })
  })
}

//properties[...] fields the add to cart payload already maps by hand
const mappedFormProperties = [
  'message',
  'Recipient email',
  'Recipient name',
  'Message',
  'Please note',
  'Send on'
]

//when a customer picks a gift option, the Wrapped app doesn't touch the cart
//itself - it injects hidden inputs into the product form and relies on the theme
//sending them with the product. the payload below is built by hand rather than
//serialised from the form, so those inputs get dropped unless we forward them,
//and the gift product silently never reaches the cart:
//
//  properties[...]  go onto the product's own line item. Wrapped intercepts the
//                   /cart/add.js response and only adds the gift product when it
//                   sees _Wrapped Bundle on the line that came back, so these are
//                   what actually trigger the gift
//  items[n][...]    are passed through untouched on the item object. the same
//                   interceptor hoists them back out into a second line item,
//                   which is the gift product itself
//
//the cart page and cart drawer are unaffected - Wrapped drives those through the
//cart API directly and never goes near this form
function getAdditionalFormFields(formData) {
  const additionalProperties = {}
  const additionalItemFields = {}

  for (const [key, value] of formData.entries()) {
    const property = key.match(/^properties\[(.+)\]$/)

    if (property) {
      if (!mappedFormProperties.includes(property[1])) {
        additionalProperties[property[1]] = value
      }
    } else if (/^items\[\d+\]\[/.test(key)) {
      additionalItemFields[key] = value
    }
  }

  return { additionalProperties, additionalItemFields }
}

//the event name that has to be set as the 'Javascript callback event' on the
//cart drawer block in the Wrapped app admin - see setWrappedRefreshCallback
const WRAPPED_CART_DRAWER_EVENT = 'wrapped:cart-drawer:refresh'

//Wrapped writes gift products straight to the cart over the Storefront API, so
//the cart drawer is showing a stale cart by the time it has finished. it never
//re-renders the drawer itself - all it does is tell us it is done - so bringing
//the drawer back in line is left to us
async function refreshCartAfterWrappedChange() {
  const cartDrawerOpen = document
    .querySelector('html')
    .classList.contains('cart-open')
  const cartPageForm = document.querySelector('.cart-page-form')

  //nothing of ours is on screen worth keeping, so let the page render the new
  //cart from scratch - the cart page is rendered by Wrapped's own block
  if (cartPageForm && !cartDrawerOpen) {
    window.location.reload()
    return
  }

  await updateCartDrawer()

  //the cart page is sitting behind the open drawer, and is stale too
  if (cartPageForm) await updateCartPage()

  updateHeaderCartCount()
}

//Wrapped signals that it has finished with the cart in two different ways, and
//we have to handle both:
//
//  processorCallback  after adding a gift product alongside a product being
//                     added to the cart. without a callback set it falls back to
//                     window.location.reload(), which would throw away the cart
//                     drawer we just opened
//
//  a custom event     after a gift is saved or removed from the gift block in
//                     the cart drawer. the block dispatches whatever event name
//                     is set as its 'Javascript callback event' in the Wrapped
//                     app admin, and does nothing else - so WRAPPED_CART_DRAWER_EVENT
//                     has to be set there for the drawer to ever update
//
//the cart page block is left reloading the page instead (its 'Refresh page when
//gift option is saved' setting), which is why nothing is configured against it
function setWrappedRefreshCallback() {
  document.addEventListener(
    WRAPPED_CART_DRAWER_EVENT,
    refreshCartAfterWrappedChange
  )

  if (!window.Wrapped?.settings) return

  window.Wrapped.settings.processorCallback = refreshCartAfterWrappedChange
}

function handleAddToCartFormEvents(addToCartForm) {
  customInputNumberListeners(addToCartForm)

  addToCartForm.addEventListener('submit', async (event) => {
    event.preventDefault()

    const formData = new FormData(addToCartForm)
    const formObject = Object.fromEntries(formData.entries())
    const variantAddedToCart = window.product.variants.find(
      (variant) => variant.id === Number(formObject.id)
    )

    const { additionalProperties, additionalItemFields } =
      getAdditionalFormFields(formData)

    await window.addProductToCart({
      variantId: formObject.id,
      sku: variantAddedToCart.sku,
      quantity: formObject.quantity,
      message: formObject['properties[message]'],
      recipientEmail: formObject['properties[Recipient email]'],
      recipientName: formObject['properties[Recipient name]'],
      giftCardMessage: formObject['properties[Message]'],
      pleaseNote: formObject['properties[Please note]'],
      sendOn: formObject['properties[Send on]'],
      additionalProperties,
      additionalItemFields
    })

    await updateCartDrawer()
    await new Promise((x) => setTimeout(x, 100)) //this is used to ensure the open-cart transition works. without it the transition is skipped
    openCartDrawer()
    updateHeaderCartCount()
  })
}

function handleCartAddFreeGift() {
  const addFreeGiftButton = document.querySelector('button#add-free-gift')

  addFreeGiftButton?.addEventListener('click', async function () {
    await window.addProductToCart({ variantId: 50510661058907, quantity: 1 })

    if (addFreeGiftButton.closest('.cart-drawer')) {
      updateCartDrawer()
    } else if (addFreeGiftButton.closest('#main-cart')) {
      updateCartPage()
    }
    updateHeaderCartCount()
  })
}

;(() => {
  document
    .getElementById('open-cart-drawer')
    .addEventListener('click', openCartDrawer)

  setWrappedRefreshCallback()

  const addToCartFormSelector = 'form[action$="/cart/add"].pdp'
  //handle add to cart forms already on the page at load time
  document
    .querySelectorAll(addToCartFormSelector)
    .forEach((form) => handleAddToCartFormEvents(form))

  const addToCartFormObserver = new MutationObserver((mutationsList) => {
    mutationsList.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (
          node.nodeType === Node.ELEMENT_NODE &&
          node.matches(addToCartFormSelector)
        ) {
          handleAddToCartFormEvents(node)
        }
        //also check child nodes
        node
          .querySelectorAll?.(addToCartFormSelector)
          .forEach((child) => handleAddToCartFormEvents(child))
      })
    })
  })

  //handle add to cart forms loaded dynamically e.g. quick view products
  addToCartFormObserver.observe(document.body, {
    childList: true,
    subtree: true
  })
})()

async function addAttributeToCart({ redeeming_voucher }) {
  try {
    const response = await fetch(
      `${window.Shopify.routes.root}cart/update.js`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          attributes: {
            redeeming_voucher
          }
        })
      }
    )
    const responseJson = await response.json()
    return responseJson
  } catch (error) {
    console.error('error adding cart attributes')
    if (window.Sentry) {
      Sentry.captureException(error, {
        tags: { function: 'addAttributeToCart' }
      })
    }
  }
}
